const getErrorProperty = (error: unknown, property: 'name' | 'message'): string => {
    if (typeof error === 'string') return property === 'message' ? error : '';
    if (!error || (typeof error !== 'object' && typeof error !== 'function')) return '';

    try {
        const value = (error as Record<'name' | 'message', unknown>)[property];
        return typeof value === 'string' ? value : '';
    } catch {
        return '';
    }
};

const getErrorName = (error: unknown): string => getErrorProperty(error, 'name');
const getErrorMessage = (error: unknown): string => getErrorProperty(error, 'message');

export const isExpectedTransitionError = (error: unknown): boolean => {
    const name = getErrorName(error);
    const message = getErrorMessage(error);
    return name === 'AbortError' ||
        name === 'InvalidStateError' ||
        message.includes('Transition was skipped') ||
        message.includes('Transition was aborted because of invalid state');
};

export const isStaleChunkError = (error: unknown): boolean => {
    const name = getErrorName(error);
    const message = getErrorMessage(error);
    return name === 'ChunkLoadError' ||
        message.includes('Failed to fetch dynamically imported module') ||
        message.includes('dynamically imported module') ||
        message.includes('Loading chunk') ||
        message.includes('does not provide an export named') ||
        message.includes('Lazy route "') ||
        message.includes('Lazy element type must resolve to a class or function') ||
        message.includes('Minified React error #306');
};

export const isKnownHarmlessBrowserDomError = (error: unknown): boolean => {
    const name = getErrorName(error);
    const message = getErrorMessage(error);

    if (
        name === 'SecurityError' &&
        message.includes("Failed to read a named property 'Element' from 'Window'") &&
        message.includes('cross-origin frame')
    ) {
        return true;
    }

    if (
        name === 'NotSupportedError' &&
        message.includes('media resource') &&
        message.includes('not suitable')
    ) {
        return true;
    }

    const isDetachedNodeRemoval =
        message.includes("Failed to execute 'removeChild' on 'Node'") &&
        message.includes('The node to be removed is not a child of this node.');

    /* Same detached-node race as removeChild, only the other direction: a page
       translator (Chrome auto-translate above all) swaps React's text nodes, so
       the reference child React commits against no longer belongs to the parent.
       Without this branch the boundary rendered "Something went wrong" with a
       login button, which read to users as being logged out mid-campaign. */
    const isDetachedNodeInsertion =
        message.includes("Failed to execute 'insertBefore' on 'Node'") &&
        (
            message.includes('The node before which the new node is to be inserted is not a child of this node.') ||
            message.includes('The child can not be found in the parent node')
        );

    if (
        isDetachedNodeRemoval ||
        isDetachedNodeInsertion ||
        (
            name === 'NotFoundError' &&
            (
                message === 'The object can not be found here.' ||
                message.includes('The node to be removed is not a child of this node.') ||
                message.includes('The node before which the new node is to be inserted is not a child of this node.')
            )
        )
    ) {
        return true;
    }

    return false;
};
